import React, { useState } from "react";
import { Mail, MapPin, Globe, Send } from "lucide-react";
import { company } from "../mock";
import { useToast } from "../hooks/use-toast";
import { submitContact } from "../api";

const Contact = React.forwardRef((props, ref) => {
  const [form, setForm] = useState({ nom: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      setForm({ nom: "", email: "", message: "" });
      toast({ title: "Message envoyé ✓", description: "Nous revenons vers vous rapidement." });
    } catch (err) {
      toast({ title: "Erreur", description: "Envoi impossible. Réessayez.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-neutral-900 border border-neutral-700 rounded-md px-4 py-3 text-sm text-white placeholder-neutral-500 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition";

  const infos = [
    { icon: MapPin, label: "Adresse", value: company.address },
    { icon: Mail, label: "Email", value: company.email },
    { icon: Globe, label: "Domaine", value: company.domain },
  ];

  return (
    <section id="contact" ref={ref} className="py-20 md:py-28 bg-neutral-950 text-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] text-brand uppercase mb-4">
              Contact
            </p>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl leading-tight">
              Parlons de <span className="text-brand">votre besoin.</span>
            </h2>
            <div className="mt-10 space-y-6">
              {infos.map((info) => (
                <div key={info.label} className="flex items-start gap-4">
                  <div className="bg-brand/10 text-brand rounded-lg p-3">
                    <info.icon size={20} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold tracking-widest text-neutral-500 uppercase">
                      {info.label}
                    </p>
                    <p className="mt-1 text-neutral-200">{info.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-neutral-900/60 border border-neutral-800 rounded-2xl p-6 md:p-8">
            <p className="text-xs font-semibold tracking-widest text-neutral-500 uppercase mb-6">
              Message rapide
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-neutral-400 mb-1.5">Nom</label>
                <input required className={inputCls} value={form.nom} onChange={(e) => update("nom", e.target.value)} placeholder="Votre nom" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-400 mb-1.5">Email</label>
                <input required type="email" className={inputCls} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="vous@societe.com" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-400 mb-1.5">Message</label>
                <textarea required rows={4} className={inputCls} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Votre message..." />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand hover:bg-brand-dark text-white font-semibold py-3.5 rounded-md flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              >
                {loading ? "Envoi..." : "Envoyer le message"}
                {!loading && <Send size={16} />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
});

export default Contact;
